import { IconDownload } from '@hulu-react-style-components/icons';
import React, { useEffect, useRef } from 'react';

import * as S from './styles';
import { useAssetDirectDownload } from './util';

type AssetDownloadButtonProps = {
  readonly filename?: string;
  readonly url: string;
};

/**
 * Renders a Button which, when clicked, will download an asset from the
 * provided *url* into the browser cache. Once complete, it will save the asset
 * using the provided *filename* value.
 */
export const AssetDownloadButton = ({ filename, url }: AssetDownloadButtonProps): React.JSX.Element => {
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const { download, clearDownload, startDownload } = useAssetDirectDownload();

  useEffect(() => {
    const anchor = anchorRef.current;
    // When the download is complete...
    if (anchor && download.completed === 100 && download.assetData) {
      // Set the blob to the anchor's href data & trigger a download
      anchor.href = download.assetData || '//:0';
      anchor.click();
      // Reset the download
      clearDownload();
    }
  }, [download.completed, download.assetData, clearDownload]);

  return (
    <>
      <S.Button disabled={download.completed !== 0} onClick={(): Promise<void> => startDownload(url)}>
        {download.completed === 0 ? <IconDownload /> : <S.ProgressWheel percent={download.completed} />}
      </S.Button>

      <a
        download={filename}
        href="//:0"
        ref={anchorRef}
        rel="noopener noreferrer"
        style={{ display: 'none' }}
        target="_blank"
      >
        Download
      </a>
    </>
  );
};
